import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Search,
  X,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit,
  Zap,
  CheckCircle,
  XCircle,
  Loader2,
  Filter,
} from 'lucide-react';
import { Card } from '../ui';
import Button from '@/components/ui/Button';
import useActivationTypeStore from '@/stores/activationTypeStore';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import { ActivationType } from '@/types/activation';

interface ActivationTypesListProps {
  onCreateClick: () => void;
  onEditClick: (activationType: ActivationType) => void;
}

const ActivationTypesList: React.FC<ActivationTypesListProps> = ({
  onCreateClick,
  onEditClick,
}) => {
  const {
    activationTypes,
    loading,
    error,
    loadActivationTypes,
  } = useActivationTypeStore();

  const { selectedCustomer } = useGlobalCustomerStore();

  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'created_at'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Load activation types when customer changes
  useEffect(() => {
    if (selectedCustomer?.id) {
      loadActivationTypes(String(selectedCustomer.id));
    }
  }, [selectedCustomer?.id, loadActivationTypes]);

  // Filter and sort types
  const filteredTypes = useMemo(() => {
    let filtered = [...activationTypes];

    // Apply search filter
    if (searchInput) {
      filtered = filtered.filter((type) =>
        type.name.toLowerCase().includes(searchInput.toLowerCase()) ||
        (type.description && type.description.toLowerCase().includes(searchInput.toLowerCase()))
      );
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter((type) => type.is_active);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter((type) => !type.is_active);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          comparison = aTime - bTime;
          break;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [activationTypes, searchInput, statusFilter, sortBy, sortOrder]);

  // Paginate results
  const paginatedTypes = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTypes.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTypes, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredTypes.length / itemsPerPage);

  const handleSort = useCallback((field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  }, [sortBy]);

  const handleClearSearch = () => {
    setSearchInput('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Calculate summary stats
  const summary = useMemo(() => {
    const total = activationTypes.length;
    const active = activationTypes.filter((t) => t.is_active).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [activationTypes]);

  const getSortIcon = (field: typeof sortBy) => {
    if (sortBy !== field) return <ChevronDown className="h-3.5 w-3.5 text-gray-400" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-blue-600" />
    );
  };

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <Button
          onClick={() => selectedCustomer?.id && loadActivationTypes(String(selectedCustomer.id))}
          className="mt-4"
          size="sm"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Activation Types</h2>
          <p className="text-xs text-gray-600">
            Manage types used to categorize activations
          </p>
        </div>
        <Button onClick={onCreateClick} size="xs">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Create Type
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="px-3 py-2 bg-gradient-to-br from-white to-slate-50/40 border-slate-100 hover:shadow-lg hover:border-slate-200 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-600">Total Types</p>
              <p className="text-base font-bold text-slate-900">
                {loading ? '...' : summary.total}
              </p>
            </div>
            <div className="p-1.5 bg-gradient-to-br from-slate-600 to-slate-700 rounded-md shadow-md group-hover:scale-110 transition-transform duration-300">
              <Zap className="h-3.5 w-3.5 text-white" />
            </div>
          </div>
        </Card>

        <Card className="px-3 py-2 bg-gradient-to-br from-white to-emerald-50/40 border-emerald-100 hover:shadow-lg hover:border-emerald-200 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-emerald-700">Active Types</p>
              <p className="text-base font-bold text-emerald-900">
                {loading ? '...' : summary.active}
              </p>
            </div>
            <div className="p-1.5 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-md shadow-md group-hover:scale-110 transition-transform duration-300">
              <CheckCircle className="h-3.5 w-3.5 text-white" />
            </div>
          </div>
        </Card>

        <Card className="px-3 py-2 bg-gradient-to-br from-white to-red-50/40 border-red-100 hover:shadow-lg hover:border-red-200 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-red-700">Inactive Types</p>
              <p className="text-base font-bold text-red-900">
                {loading ? '...' : summary.inactive}
              </p>
            </div>
            <div className="p-1.5 bg-gradient-to-br from-red-600 to-red-700 rounded-md shadow-md group-hover:scale-110 transition-transform duration-300">
              <XCircle className="h-3.5 w-3.5 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search types..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as typeof statusFilter);
                setCurrentPage(1);
              }}
              className="pl-8 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            <span className="ml-2 text-sm text-gray-600">Loading activation types...</span>
          </div>
        ) : filteredTypes.length === 0 ? (
          <div className="text-center py-12">
            <Zap className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {searchInput || statusFilter !== 'all'
                ? 'No activation types match your filters'
                : 'No activation types found'}
            </p>
            {!searchInput && statusFilter === 'all' && (
              <Button onClick={onCreateClick} size="sm" className="mt-4">
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Create First Type
              </Button>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('name')}
                  className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    Name
                    {getSortIcon('name')}
                  </div>
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th
                  onClick={() => handleSort('created_at')}
                  className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    Created
                    {getSortIcon('created_at')}
                  </div>
                </th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedTypes.map((type) => (
                <tr
                  key={type.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="p-1.5 bg-blue-100 rounded-md mr-2">
                        <Zap className="h-3.5 w-3.5 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{type.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 line-clamp-1">
                      {type.description || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        type.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {type.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-500">
                      {type.created_at ? new Date(type.created_at).toLocaleDateString() : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => onEditClick(type)}
                      className="inline-flex items-center p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Edit activation type"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-500">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredTypes.length)} of{' '}
              {filteredTypes.length} types
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  return (
                    page === 1 ||
                    page === totalPages ||
                    Math.abs(page - currentPage) <= 1
                  );
                })
                .map((page, index, arr) => (
                  <React.Fragment key={page}>
                    {index > 0 && arr[index - 1] !== page - 1 && (
                      <span className="px-1 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => handlePageChange(page)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ActivationTypesList;
